import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { toast } from 'sonner';
import { useDebounceValue } from '../../../hooks/useDebounceValue';
import {
	ALL_IMAGE_SOURCES,
	downloadLibraryItem,
	getImageLibraryFilters,
	isLibraryDocument,
	toFolderQueryParams,
	toImageLibraryKindFilter,
	toImageSourceFilter,
	useImageLibraryKeywordsQuery,
	useImageLibraryQuery,
	useRemoveFromImageLibraryMutation,
	useRenameImageLibraryEntryMutation,
	useUpdateImageLibraryKeywordsMutation,
	IImageLibraryItem,
} from '../../../queries/ImageLibrary';
import { Button } from '../Button';
import { FolderRail } from '../FolderRail';
import { ImageLibraryGrid } from '../ImageLibraryGrid';
import { Input } from '../Input';
import { TabToggle } from '../TabToggle';
import { UnifiedImageDialog } from '../UnifiedImageDialog';
import { AddFilesDialog } from './AddFilesDialog';
import { FolderCombobox } from './FolderCombobox';
import { styleSheet } from './styles';

const MAX_LIBRARY_UPLOAD_IMAGES = 25;

export interface ImageLibraryManagerProps {
	/** Set by the admin app so reads and writes hit the customer's library, not the admin's own account */
	impersonationContext?: Api.IImpersonationContext;
	heading: string;
	description: string;
	emptyTitle: string;
	emptyDescription: string;
	/**
	 * Lets the account keep reference documents (brand guides and the like) alongside its images. Off by
	 * default so a surface has to opt in rather than inherit a file browser it did not ask for.
	 */
	allowFiles?: boolean;
}

/**
 * Browse, search, organise, and clean up an account's image library. Shared by the customer-facing
 * Campaigns tab and the admin account page, which differ only in copy and whether calls carry an
 * impersonation context.
 *
 * This is the only place images can leave the library. Everything else in the product adds to it, which is
 * what makes a cleanup surface necessary rather than nice to have.
 */
export function ImageLibraryManager({
	impersonationContext,
	heading,
	description,
	emptyTitle,
	emptyDescription,
	allowFiles = false,
}: ImageLibraryManagerProps) {
	const [searchTerm, setSearchTerm] = React.useState('');
	const [sourceFilter, setSourceFilter] = React.useState<string>(ALL_IMAGE_SOURCES);
	const [selectedFolder, setSelectedFolder] = React.useState<string | null>(null);
	const [selectedIds, setSelectedIds] = React.useState<Set<string>>(new Set());
	const [confirmingRemove, setConfirmingRemove] = React.useState(false);
	// The mutation's isPending flips once per image, so a batch would flicker the button.
	const [isRemovingBatch, setIsRemovingBatch] = React.useState(false);
	const [isFilingBatch, setIsFilingBatch] = React.useState(false);
	const [isChoosingFolder, setIsChoosingFolder] = React.useState(false);
	const [uploadDialogOpen, setUploadDialogOpen] = React.useState(false);
	const [filesDialogOpen, setFilesDialogOpen] = React.useState(false);

	const debouncedSearchTerm = useDebounceValue(searchTerm);

	const kind = toImageLibraryKindFilter(sourceFilter, allowFiles);

	const libraryQuery = useImageLibraryQuery({
		impersonationContext,
		kind,
		query: debouncedSearchTerm,
		source: toImageSourceFilter(sourceFilter),
		...toFolderQueryParams(selectedFolder),
	});

	const keywordsQuery = useImageLibraryKeywordsQuery({ impersonationContext, kind });

	const removeMutation = useRemoveFromImageLibraryMutation();

	const renameMutation = useRenameImageLibraryEntryMutation({
		onError: () => toast.error('Could not rename this image. Try again.'),
	});

	const keywordsMutation = useUpdateImageLibraryKeywordsMutation({
		onError: () => toast.error('Could not update folders. Try again.'),
	});

	const items = libraryQuery.data?.pages?.flatMap(page => page.values ?? []) ?? [];
	const folders = keywordsQuery.data ?? [];
	const folderNames = folders.map(folder => folder.keyword);
	const isFiltered = !!debouncedSearchTerm || sourceFilter !== ALL_IMAGE_SOURCES || !!selectedFolder;
	const selectionNoun = allowFiles ? 'item' : 'image';
	// What the current filter is actually showing, so an empty Files tab does not say "no matching images".
	const filteredNoun = kind === 'document' ? 'file' : selectionNoun;

	const changeFilter = (apply: () => void) => {
		// A selection is only meaningful against the cards it was made on; carrying it across a filter
		// change would let a bulk action quietly apply to a different set than the count claims.
		setSelectedIds(new Set());
		setIsChoosingFolder(false);
		setConfirmingRemove(false);
		apply();
	};

	const toggleSelected = (item: IImageLibraryItem) => {
		setSelectedIds(previous => {
			const next = new Set(previous);
			if (next.has(item.id)) {
				next.delete(item.id);
			} else {
				next.add(item.id);
			}
			return next;
		});
	};

	const setItemFolders = (item: IImageLibraryItem, keywords: string[]) =>
		keywordsMutation.mutateAsync({ impersonationContext, itemId: item.id, keywords });

	const fileSelectionIntoFolder = async (folder: string) => {
		// Re-filing something already in the folder would duplicate the keyword.
		const toFile = items.filter(
			item =>
				selectedIds.has(item.id) && !item.keywords?.some(keyword => keyword.toLowerCase() === folder.toLowerCase())
		);

		setIsChoosingFolder(false);

		if (!toFile.length) {
			toast.success(`Already in "${folder}"`);
			return;
		}

		setIsFilingBatch(true);
		const outcomes = await Promise.allSettled(
			toFile.map(item => setItemFolders(item, [...(item.keywords ?? []), folder]))
		);
		setIsFilingBatch(false);

		const failed = outcomes.filter(outcome => outcome.status === 'rejected').length;
		if (failed) {
			toast.error(`Could not file ${failed} of ${outcomes.length}. Try again.`);
			return;
		}

		setSelectedIds(new Set());
		toast.success(`Added to "${folder}"`);
	};

	const removeSelected = async () => {
		const ids = [...selectedIds];
		setIsRemovingBatch(true);
		const outcomes = await Promise.allSettled(
			ids.map(itemId => removeMutation.mutateAsync({ impersonationContext, itemId }))
		);
		const failed = outcomes.filter(outcome => outcome.status === 'rejected').length;

		setIsRemovingBatch(false);
		setConfirmingRemove(false);

		if (failed) {
			// Keep the failures selected, otherwise "try the rest again" means hunting for them by hand.
			setSelectedIds(new Set(ids.filter((_, index) => outcomes[index].status === 'rejected')));
			toast.error(
				failed === ids.length
					? `Could not remove those ${selectionNoun}s. Try again.`
					: `Removed ${ids.length - failed} of ${ids.length}. Try the rest again.`
			);
			return;
		}

		setSelectedIds(new Set());

		toast.success(
			ids.length === 1 ? 'Removed from the library' : `Removed ${ids.length} ${selectionNoun}s from the library`
		);
	};

	const download = (item: IImageLibraryItem) =>
		downloadLibraryItem(item).catch(() =>
			toast.error(`Could not download this ${isLibraryDocument(item) ? 'file' : 'image'}. Try again.`)
		);

	return (
		<div className={css(styleSheet.container)}>
			<header className={css(styleSheet.header)}>
				<div>
					<h2 className={css(styleSheet.heading)}>{heading}</h2>
					<p className={css(styleSheet.subheading)}>{description}</p>
				</div>
				<div className={css(styleSheet.headerActions)}>
					{allowFiles ? (
						<Button onClick={() => setFilesDialogOpen(true)} variant='secondary'>
							Add files
						</Button>
					) : null}
					<Button onClick={() => setUploadDialogOpen(true)} variant='primary'>
						Add images
					</Button>
				</div>
			</header>

			<div className={css(styleSheet.controls)}>
				<Input
					autoComplete='off'
					id='image-library-manager-search'
					onChange={e => changeFilter(() => setSearchTerm(e.target.value))}
					placeholder={allowFiles ? 'Search images and files' : 'Search images'}
					styles={[styleSheet.searchInput]}
					type='text'
					value={searchTerm}
				/>
				<TabToggle
					items={getImageLibraryFilters(allowFiles)}
					onValueChange={value => changeFilter(() => setSourceFilter(value))}
					value={sourceFilter}
				/>
			</div>

			<FolderRail
				folders={folders}
				onSelectFolder={folder => changeFilter(() => setSelectedFolder(folder))}
				selectedFolder={selectedFolder}
			/>

			{selectedIds.size > 0 && (
				<div className={css(styleSheet.selectionBar)}>
					<span className={css(styleSheet.selectionCount)}>
						{selectedIds.size === 1 ? `1 ${selectionNoun} selected` : `${selectedIds.size} ${selectionNoun}s selected`}
					</span>
					{confirmingRemove ? (
						<>
							<Button onClick={() => setConfirmingRemove(false)} variant='secondary'>
								Keep them
							</Button>
							<Button isLoading={isRemovingBatch} onClick={removeSelected} variant='destructive'>
								Remove from library
							</Button>
						</>
					) : isChoosingFolder ? (
						<>
							<div className={css(styleSheet.folderPicker)}>
								<FolderCombobox
									folders={folderNames}
									onClose={() => setIsChoosingFolder(false)}
									onSelect={fileSelectionIntoFolder}
								/>
							</div>
							<Button onClick={() => setIsChoosingFolder(false)} variant='secondary'>
								Cancel
							</Button>
						</>
					) : (
						<>
							<Button onClick={() => setSelectedIds(new Set())} variant='secondary'>
								Clear selection
							</Button>
							<Button isLoading={isFilingBatch} onClick={() => setIsChoosingFolder(true)} variant='secondary'>
								Add to folder
							</Button>
							<Button onClick={() => setConfirmingRemove(true)} variant='destructive-secondary'>
								Remove from library
							</Button>
						</>
					)}
				</div>
			)}

			<ImageLibraryGrid
				emptyDescription={isFiltered ? 'Try a different search term or filter.' : emptyDescription}
				emptyTitle={isFiltered ? `No matching ${filteredNoun}s` : emptyTitle}
				hasNextPage={libraryQuery.hasNextPage}
				isFetchingNextPage={libraryQuery.isFetchingNextPage}
				isError={libraryQuery.isError}
				isLoading={libraryQuery.isLoading}
				items={items}
				onItemClick={toggleSelected}
				onLoadMore={() => libraryQuery.fetchNextPage()}
				onRemoveFolder={(item, folder) =>
					keywordsMutation.mutate({
						impersonationContext,
						itemId: item.id,
						keywords: (item.keywords ?? []).filter(keyword => keyword !== folder),
					})
				}
				onDownload={download}
				onRetry={() => libraryQuery.refetch()}
				onRename={(item, displayName) => renameMutation.mutate({ displayName, impersonationContext, itemId: item.id })}
				selectedIds={selectedIds}
			/>

			<AddFilesDialog
				impersonationContext={impersonationContext}
				onOpenChange={setFilesDialogOpen}
				open={filesDialogOpen}
			/>

			<UnifiedImageDialog
				addUploadsToLibrary
				allowMultipleUpload
				impersonationContext={impersonationContext}
				initialTab='upload'
				// Bulk brand photos are the point of this button, so the count is generous; the client batches
				// the selection to stay under the request size limit rather than letting a big pick fail.
				maxUploadImages={MAX_LIBRARY_UPLOAD_IMAGES}
				onImageSelected={() => setUploadDialogOpen(false)}
				onMultipleImagesSelected={() => setUploadDialogOpen(false)}
				onOpenChange={setUploadDialogOpen}
				open={uploadDialogOpen}
				showFreeImagesTab={false}
			/>
		</div>
	);
}
